import React from "react";
import {
	Grid,
	Icon,
	IconButton,
	Tooltip,
	MUIDataTable,
	SelectableRows,
	FilterType,
	TableRow,
	TableCell,
} from "../../common/MaterialComponent";
import Shared from "../../common/shared";
import { useGetComponentStringsContext } from "../../hooks/useGetComponentStrings";
import "./TemplateListDataGrid.css";
import {
	FetchBatchMessageDetailModel,
	PreSubmissionConfigurationModel,
} from "../../models/BatchMessage/BatchMessageDetailModel";
import { TemplateListDataGridExpandedRow } from "./TemplateListDataGridExpandedRow/TemplateListDataGridExpandedRow";
import { GetBatchMessagesForTemplates } from "../../api/batchNotificationController";
import BatchMessageSchedulingDetailModel from "../../models/BatchMessage/BatchMessageSchedulingDetailModel";

export interface TemplateListDataGridProp {
	templateList: PreSubmissionConfigurationModel[];
	displayStatusColumn: boolean;
	scheduledTemplate: boolean;
	title: string;
	onNotificationIdChange: (
		preSubmissionConfigurationDetail: PreSubmissionConfigurationModel
	) => void;
	onApproveTemplateSelectedId: (
		preSubmissionConfigurationDetail: PreSubmissionConfigurationModel
	) => void;
	onRejectTemplateSelectedId: (
		preSubmissionConfigurationDetail: PreSubmissionConfigurationModel
	) => void;
	messageCancelEnabled: boolean;
	templateUpdateRequired: (val: boolean) => void;
	setMounted: (val: boolean) => void;
}

export const TemplateListDataGrid: React.FC<TemplateListDataGridProp> = ({
	templateList,
	displayStatusColumn,
	scheduledTemplate,
	title,
	onNotificationIdChange,
	onApproveTemplateSelectedId,
	onRejectTemplateSelectedId,
	messageCancelEnabled,
	templateUpdateRequired,
	setMounted,
}) => {
	const [rowsExpanded, setRowsExpanded] = React.useState<number[]>([]);

	const [displayedTemplateList, setDisplayedTemplateList] = React.useState<
		PreSubmissionConfigurationModel[]
	>([]);

	const [batchMessages, setBatchMessages] = React.useState<
		BatchMessageSchedulingDetailModel[]
	>([]);

	const strings = useGetComponentStringsContext().TemplateListDataGrid;

	React.useEffect(() => {
		setDisplayedTemplateList([]);
		setBatchMessages([]);
		const getBatchMessageForTemplates = async () => {
			try {
				let req: FetchBatchMessageDetailModel[] = [];
				templateList.forEach((template) => {
					let messageIds: string[] = [];
					if (
						template.nextScheduledRuns !== null &&
						typeof template.nextScheduledRuns === "string"
					) {
						messageIds = JSON.parse(template.nextScheduledRuns);
						req.push(
							new FetchBatchMessageDetailModel(
								template.partitionKey,
								messageIds
							)
						);
					}
				});
				const res = await GetBatchMessagesForTemplates(req);
				if (res.error) {
					Shared.showSnackbarMessage("Failed to retrieve batch messages");
				} else {
					templateList.forEach((template) => {
						if (template.status === "Pending") {
							setDisplayedTemplateList((prevDispTempList) => [
								...prevDispTempList,
								template,
							]);
						} else {
							if (res.responseObj) {
								res.responseObj.forEach((bms) => {
									if (bms.preSubmissionConfigurationId !== null) {
										if (template.rowKey === bms.preSubmissionConfigurationId) {
											setDisplayedTemplateList((prevDispTempList) => [
												...prevDispTempList,
												template,
											]);
										}
									}
								});
							}
						}
					});
					setBatchMessages(res.responseObj ? res.responseObj : []);
				}
			} catch (err) {
				console.error(err);
			}
		};
		if (templateList.length > 0) {
			getBatchMessageForTemplates();
		}
	}, [templateList]);

	const getTemplateStatus = (value: string) => {
		return value ? value : "-";
	};

	const columns = () => {
		const statusColumn = {
			name: "status",
			label: strings.status,
			options: {
				filterType: "dropdown" as FilterType,
				customBodyRender: (value: any) => (
					<span>{getTemplateStatus(value)}</span>
				),
			},
		};
		const columnList = [
			{
				name: "title",
				label: strings.title,
				options: {
					filterType: "dropdown" as FilterType,
				},
			},
			{
				name: "country",
				label: strings.countryName,
				options: {
					filterType: "dropdown" as FilterType,
				},
			},
			{
				name: "batchMessageType",
				label: strings.batchMessageType,
				options: {
					filterType: "dropdown" as FilterType,
					customBodyRender: (value: any, tableMeta: any) => {
						const templateDetail =
							displayedTemplateList[
								tableMeta.currentTableData[tableMeta.rowIndex].index
							];
						return templateDetail.batchMessageType
							? templateDetail.batchMessageType
							: templateDetail.ivrFlowType;
					},
				},
			},
			{
				name: "type",
				label: strings.templateType,
				options: {
					filterType: "dropdown" as FilterType,
				},
			},
			{
				name: "totalCost",
				label: strings.approximateCost,
				options: {
					filter: false,
					customBodyRender: (value: any, tableMeta: any) => {
						const templateDetail =
							displayedTemplateList[
								tableMeta.currentTableData[tableMeta.rowIndex].index
							];
						return !templateDetail.africasTalkingEnabled
							? Shared.getFormatedCurrencyValue(
									value ?? 0,
									templateDetail.currency
							  )
							: "N/A";
					},
				},
			},
			{
				name: "Action",
				label: strings.action,
				options: {
					filter: false,
					sort: false,
					empty: true,
					customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
						return (
							<div>
								{scheduledTemplate ? (
									<Tooltip title={strings.previewBtnTooltip}>
										<IconButton
											className="editIcon"
											onClick={() => {
												onNotificationIdChange(
													displayedTemplateList[
														tableMeta.currentTableData[tableMeta.rowIndex].index
													]
												);
											}}
										>
											<Icon color="primary">visibility</Icon>
										</IconButton>
									</Tooltip>
								) : (
									<Tooltip title={strings.editBtnTooltip}>
										<IconButton
											data-testid="editTemplate"
											className="editIcon"
											onClick={() => {
												onNotificationIdChange(
													displayedTemplateList[
														tableMeta.currentTableData[tableMeta.rowIndex].index
													]
												);
											}}
										>
											<Icon color="primary">edit</Icon>
										</IconButton>
									</Tooltip>
								)}
								{!displayStatusColumn && (
									<>
										<Tooltip title={strings.approveBtnTooltip}>
											<IconButton
												data-testid="approveTemplate"
												className="btnIcon"
												onClick={() =>
													onApproveTemplateSelectedId(
														displayedTemplateList[
															tableMeta.currentTableData[tableMeta.rowIndex]
																.index
														]
													)
												}
											>
												<Icon fontSize="large" className="approveButton">
													check_circle
												</Icon>
											</IconButton>
										</Tooltip>
										<Tooltip title={strings.rejectBtnTooltip}>
											<IconButton
												data-testid="rejectTemplate"
												className="btnIcon"
												onClick={() =>
													onRejectTemplateSelectedId(
														displayedTemplateList[
															tableMeta.currentTableData[tableMeta.rowIndex]
																.index
														]
													)
												}
											>
												<Icon fontSize="large" className="rejectButton">
													cancel
												</Icon>
											</IconButton>
										</Tooltip>
									</>
								)}
							</div>
						);
					},
				},
			},
		];
		if (displayStatusColumn) {
			columnList.splice(columnList.length - 1, 0, statusColumn as any);
		}
		return columnList;
	};

	const renderExpandableRow = (
		rowData: string[],
		rowMeta: { dataIndex: number }
	) => {
		const item = displayedTemplateList[rowMeta.dataIndex];
		if (!item) {
			return <TableRow></TableRow>;
		}
		return (
			<TableRow>
				<TableCell colSpan={rowData.length + 1}>
					<TemplateListDataGridExpandedRow
						key={item.rowKey}
						item={item}
						batchMessages={batchMessages.find((b) => {
							return b.preSubmissionConfigurationId === item.rowKey;
						})}
						getIsBatchMessageTypeSMS={item.batchMessageType === "SMS"}
						getIsBatchMessageTypeWhatsApp={item.batchMessageType === "WhatsApp"}
						messageCancelEnabled={messageCancelEnabled}
						country={item.country}
						templateUpdateRequired={templateUpdateRequired}
						setMounted={setMounted}
					/>
				</TableCell>
			</TableRow>
		);
	};

	const onRowsExpand = (current: { dataIndex: number }[]) => {
		if (current && current.length > 0) {
			const dataIndex = current[0].dataIndex;
			setRowsExpanded((prevState) => {
				const existingIndex = prevState.findIndex((e) => e === dataIndex);
				const expandedDataIndexs: number[] = [];
				if (existingIndex === -1) {
					//not already open, open it
					expandedDataIndexs.push(dataIndex);
				} //otherwise it will close
				expandedDataIndexs.concat(prevState);
				return expandedDataIndexs;
			});
		}
	};

	const options = {
		print: false,
		selectableRows: "none" as SelectableRows,
		download: false,
		expandableRows: true,
		expandableRowsOnClick: true,
		onRowExpansionChange: onRowsExpand,
		renderExpandableRow: renderExpandableRow,
		rowsExpanded: rowsExpanded,
		expandableRowsHeader: false,
	};

	return (
		<div data-testid="templateListContainer">
			<Grid container spacing={3}>
				<Grid item xs={12} className="notificationList">
					<MUIDataTable
						title={title}
						data={displayedTemplateList}
						columns={columns()}
						options={options}
					/>
				</Grid>
			</Grid>
		</div>
	);
};
